import * as d3 from 'd3';

import BenchlingConfig from 'BenchlingConfig';
import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import {SINGLE_ANNOTATION_RADIUS} from 'molbio/maps/BiointelligentLinksConstants';
import {RingLayout} from 'molbio/maps/circular/CircularLayout';
import LabeledSegmentsRingView, {LabeledSegmentsRingData} from 'molbio/maps/circular/LabeledSegmentsRingView';
import {SegmentRingSelection} from 'molbio/maps/circular/SegmentsRingView';

/**
 * Shared class to render the annotation part of biointelligent link visualizations on the circular map.
 * It handles drawing the arcs and labeling the links, but individual link types should extend this class
 * and implement enterRing in order to create the necessary SVG paths and then handleDrawContents in order to
 * draw the link design inside of the arcs.
 */
abstract class CircularBiointelligentLinkAnnotationsView<
  T extends LabeledSegmentsRingData,
> extends LabeledSegmentsRingView<RingLayout, T> {
  override contextMenuOptions(d: T): MenuChoicesOptions {
    return {tabstate: this.tabstate};
  }

  override arc(d: T): d3.Arc<unknown, d3.DefaultArcObject> {
    const arc = d3.arc();
    if (
      BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS &&
      BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS
    ) {
      arc.cornerRadius(SINGLE_ANNOTATION_RADIUS);
    }
    return arc;
  }

  override setConstraints(outerRadius: number): void {
    this.setOuterRadius(outerRadius);
  }

  override redrawPlasmid(): boolean {
    return true;
  }

  override storeBounds(rings?: number | null): void {
    if (rings == null) {
      ({rings} = this);
    }
    this.rings = rings;
    const innerRadius = this.getOuterRadius() - this.layout.get('ringHeight') * this.rings!;
    this.layout.set('innerRadius', innerRadius);
    this.updateRadius();
  }

  override zeroBounds(): void {
    this.layout.set('innerRadius', this.layout.get('outerRadius'));
    this.updateRadius();
  }

  override layerBounds(layer: number): {innerRadius: number; outerRadius: number} {
    return {
      innerRadius: this.radiusIn(layer + 1),
      outerRadius: this.radiusIn(layer) - this.layout.vMargin(),
    };
  }

  override baseLine(layer: number): number {
    return this.radiusIn(layer + 1);
  }

  override enterRing(enter: SegmentRingSelection<T>): SegmentRingSelection<T> {
    super.enterRing(enter);
    const contentsG = enter.append<SVGGElement>('svg:g').classed('ring-contents', true).merge(enter);
    return contentsG;
  }

  override eachRing(root: SegmentRingSelection<T>): void {
    super.eachRing(root);
    if (this.handleDrawContents) {
      this.handleDrawContents(root.select('.ring-contents'));
    }
  }

  override forceOutsideLabels(): boolean {
    return BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS;
  }

  /**
   * Implement to create the necessary SVG path at .biointelligent-link-inner-details.
   */
  abstract handleDrawContents(selector: d3.Selection<SVGGElement, T, SVGGElement, unknown>): void;
}

export default CircularBiointelligentLinkAnnotationsView;
